import { useCallback, useEffect, useRef, useState } from 'react';
import { getApiUrl } from '../config/api';
import type {
  ContractAnalyticsSummary,
  ContractRef,
  MarketFlowRow,
} from '../types/contractAnalytics';

function contractQuery(c: ContractRef): string {
  const p = new URLSearchParams({
    symbol: c.symbol,
    strike: String(c.strike),
    expiry: c.expiry.slice(0, 10),
    optionType: c.optionType,
  });
  return p.toString();
}

export function useContractAnalytics() {
  const abortRef = useRef<AbortController | null>(null);

  const fetchContractSummary = useCallback(
    async (contract: ContractRef): Promise<ContractAnalyticsSummary> => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      const res = await fetch(
        getApiUrl(`/api/contract-analytics/summary?${contractQuery(contract)}`),
        { signal: ac.signal },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error || res.statusText);
      }
      return res.json();
    },
    [],
  );

  const fetchContractScore = useCallback(async (contract: ContractRef) => {
    const res = await fetch(
      getApiUrl(`/api/contract-analytics/score?${contractQuery(contract)}`),
    );
    if (!res.ok) throw new Error('Score fetch failed');
    return res.json();
  }, []);

  const fetchMarketPanels = useCallback(async (symbol: string) => {
    const sym = symbol.trim().toUpperCase() || 'SPY';
    const [topFlow, bigFlow, flowSummary] = await Promise.all([
      fetch(getApiUrl('/api/analytics/market/top-flow?limit=20')).then((r) =>
        r.ok ? r.json() : { data: [] },
      ),
      fetch(
        getApiUrl(
          `/api/analytics/market/big-flow?symbol=${encodeURIComponent(sym)}&limit=10`,
        ),
      ).then((r) => (r.ok ? r.json() : { data: [] })),
      fetch(
        getApiUrl(
          `/api/analytics/market/flow-summary?symbol=${encodeURIComponent(sym)}`,
        ),
      ).then((r) => (r.ok ? r.json() : { data: {} })),
    ]);
    return {
      topFlow: (topFlow.data || []) as MarketFlowRow[],
      bigFlow: (bigFlow.data || []) as MarketFlowRow[],
      flowSummary: flowSummary.data || flowSummary,
      symbol: sym,
    };
  }, []);

  useEffect(() => () => abortRef.current?.abort(), []);

  return { fetchContractSummary, fetchContractScore, fetchMarketPanels };
}

export function useContractSummaryState(contract: ContractRef | null) {
  const { fetchContractSummary } = useContractAnalytics();
  const [data, setData] = useState<ContractAnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!contract) {
      setData(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchContractSummary(contract)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message || 'Failed to load summary');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    contract?.symbol,
    contract?.strike,
    contract?.expiry,
    contract?.optionType,
    fetchContractSummary,
  ]);

  return { data, loading, error, refetch: () => contract && fetchContractSummary(contract).then(setData) };
}
